import type { IconName } from '../components/icons';
import { fmtDate, fmtTime, localDate, timeZone } from './format';
import { isOpen } from './tickets';

// Darstellungshilfen für Rückfragen, Aufgaben und Benachrichtigungen: Fälligkeit in Worten, Gruppen im Posteingang,
// Initialen, „vor 5 Min.“ und das Symbol je Benachrichtigungsart.

export type Tone = 'error' | 'warn' | 'info' | 'muted';

interface DueLike {
  due_at?: string | null;
  overdue?: boolean;
  status: Parameters<typeof isOpen>[0];
  priority?: string;
}

const weekdayDate = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'short', day: '2-digit', month: '2-digit' });

// Kalendertage von heute bis zum Datum (Europe/Berlin).
function dayDiff(d: Date, now: Date): number {
  return Math.round((Date.parse(localDate(d)) - Date.parse(localDate(now))) / 86_400_000);
}

// since: Dauer in Worten („3 Std.“, „2 Tagen“) – für „überfällig seit …“ und „vor …“.
export function since(d: Date, now: Date = new Date()): string {
  const min = Math.max(1, Math.round((now.getTime() - d.getTime()) / 60_000));
  if (min < 60) return `${min} Min.`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} Std.`;
  const days = Math.round(h / 24);
  return days === 1 ? '1 Tag' : `${days} Tagen`;
}

// ago: Zeitpunkt in der Vergangenheit, kurz („vor 5 Min.“, „gestern, 14:05“, „Mo., 28.09.“).
export function ago(v: string, now: Date = new Date()): string {
  const d = new Date(v);
  const diff = dayDiff(d, now);
  if (diff === 0) return now.getTime() - d.getTime() < 3_600_000 ? `vor ${since(d, now)}` : `heute, ${fmtTime(v)}`;
  if (diff === -1) return `gestern, ${fmtTime(v)}`;
  return weekdayDate.format(d);
}

// dueInfo: Fälligkeit in Worten mit Ton (überfällig rot, heute gelb, morgen blau).
export function dueInfo(t: DueLike, now: Date = new Date()): { label: string; tone: Tone } | null {
  if (!t.due_at) return null;
  const due = new Date(t.due_at);
  const open = isOpen(t.status);
  const diff = dayDiff(due, now);
  const time = fmtTime(t.due_at);
  if (open && (t.overdue || due.getTime() < now.getTime())) return { label: `überfällig seit ${since(due, now)}`, tone: 'error' };
  if (diff === 0) return { label: `heute, ${time}`, tone: open ? 'warn' : 'muted' };
  if (diff === 1) return { label: `morgen, ${time}`, tone: open ? 'info' : 'muted' };
  if (diff > 1 && diff < 7) return { label: `${weekdayDate.format(due)}, ${time}`, tone: 'muted' };
  return { label: fmtDate(due), tone: 'muted' };
}

export interface Bucket {
  key: 'urgent' | 'overdue' | 'today' | 'week' | 'later' | 'none';
  label: string;
  tone: Tone;
}

export const buckets: Bucket[] = [
  { key: 'urgent', label: 'Dringend', tone: 'error' },
  { key: 'overdue', label: 'Überfällig', tone: 'error' },
  { key: 'today', label: 'Heute fällig', tone: 'warn' },
  { key: 'week', label: 'Nächste 7 Tage', tone: 'info' },
  { key: 'later', label: 'Später', tone: 'muted' },
  { key: 'none', label: 'Ohne Fälligkeit', tone: 'muted' },
];

// bucketOf: Gruppe im Posteingang – Dringendes zuerst, dann nach Fälligkeit.
export function bucketOf(t: DueLike, now: Date = new Date()): Bucket['key'] {
  if (t.priority === 'URGENT') return 'urgent';
  if (!t.due_at) return 'none';
  const due = new Date(t.due_at);
  if (t.overdue || due.getTime() < now.getTime()) return 'overdue';
  const diff = dayDiff(due, now);
  if (diff === 0) return 'today';
  return diff < 7 ? 'week' : 'later';
}

// Bereiche nach Art der Arbeit (Dominik 14.10.2026): Was zu tun ist, steht zusammen – Kontaktstopp zuerst, dann Kunden und
// Termine, Daten, Klärungen aus der Akquise, Rückfragen im Team, weitere Aufgaben. Innerhalb eines Bereichs: Dringendes,
// Überfälliges, dann nach Fälligkeit.
export interface WorkArea {
  key: 'contact_stop' | 'customer' | 'data' | 'clarify' | 'team' | 'tasks';
  label: string;
  tone: Tone;
}

export const workAreas: WorkArea[] = [
  { key: 'contact_stop', label: 'Kontaktstopp prüfen', tone: 'error' },
  { key: 'customer', label: 'Kunden & Termine', tone: 'warn' },
  { key: 'data', label: 'Daten korrigieren', tone: 'info' },
  { key: 'clarify', label: 'Klärungen aus der Akquise', tone: 'info' },
  { key: 'team', label: 'Rückfragen im Team', tone: 'muted' },
  { key: 'tasks', label: 'Weitere Aufgaben', tone: 'muted' },
];

interface WorkLike {
  category: string;
  kind: string;
  source: string;
  subject: string;
  appointment_id?: string | null;
}

const appointmentCategories = ['CANCELLATION_CALL', 'APPOINTMENT_HANDBACK', 'BOOKING_CLARIFICATION'];

export function workAreaOf(t: WorkLike): WorkArea['key'] {
  if (t.category === 'CONTACT_STOP') return 'contact_stop';
  if (t.category === 'DATA_QUALITY') return 'data';
  if (
    appointmentCategories.includes(t.category) ||
    (t.source === 'EMAIL' && t.kind === 'REQUEST' && !t.subject.startsWith('Antwort per E-Mail von')) ||
    t.subject.startsWith('Verpasster Anruf') ||
    t.subject.startsWith('Sprachnachricht von')
  ) {
    return 'customer';
  }
  if (t.source === 'CALL' && t.kind === 'TASK') return 'clarify';
  if (t.kind === 'REQUEST') return 'team';
  return 'tasks';
}

// byUrgency: Dringend, überfällig, nach Fälligkeit, ohne Fälligkeit zuletzt.
export function byUrgency(a: DueLike, b: DueLike, now: Date = new Date()): number {
  const rank = (t: DueLike) => (t.priority === 'URGENT' ? 0 : t.due_at && (t.overdue || Date.parse(t.due_at) < now.getTime()) ? 1 : t.due_at ? 2 : 3);
  const r = rank(a) - rank(b);
  if (r !== 0) return r;
  return (a.due_at ? Date.parse(a.due_at) : Infinity) - (b.due_at ? Date.parse(b.due_at) : Infinity);
}

export function initials(name: string): string {
  const parts = name.replace(/^Team\s+/, '').split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

// notificationIcon: Symbol je Benachrichtigungsart (Glocke).
export function notificationIcon(type: string): IconName {
  if (type === 'URGENT_TICKET' || type.startsWith('OUTLOOK_')) return 'warning';
  if (type.startsWith('APPOINTMENT_') && type !== 'APPOINTMENT_HANDBACK') return 'calendar';
  if (type === 'FOLLOW_UP_DUE') return 'clock';
  if (type === 'INBOUND_MISSED') return 'phone';
  if (type === 'AI_REVIEW_REQUIRED') return 'sparkle';
  if (type === 'ADDRESS_UNCONFIRMED') return 'pin';
  if (type === 'EMAIL_RECEIVED') return 'mail';
  if (type === 'PRIVACY_COMPLETED') return 'lock';
  if (type === 'WORKLIST') return 'note';
  return 'message';
}
